/* @flow */
import { pipe } from 'ramda';
import * as entititesTypes from 'src/shared/domain/base/action/entityActionTypes';
import * as types from 'src/shared/domain/user/action/userActionTypes';
import {
  createReducer,
  patchEntities,
} from 'src/shared/core/util/reducerUtils';

import { defaultLocalizableKey } from '@braindate/domain/lib/base/l10n/setting';
import { getLocalizedData } from '@braindate/domain/lib/base/l10n/util';
import {
  getKeywordEnabled,
  getKeywordLabel,
} from 'src/shared/domain/keyword/util/keywordUtils';

export const keywordsSync = (
  oldState: ?Object,
  newState: ?Object,
  key: string,
) => {
  if (!oldState || !newState) return newState;
  const body = newState[defaultLocalizableKey];
  if (!body) return newState;
  const langKeys = Object.keys(body);

  const newBody = { ...body };

  langKeys.forEach((locale) => {
    const body = getLocalizedData(newState, locale);
    const oldBody = getLocalizedData(oldState, locale);

    const keywords = body[key];
    const oldKeywords = oldBody[key];

    if (keywords && oldKeywords) {
      const computedKeywords = keywords.map((keyword) => {
        const enabled = getKeywordEnabled(keyword);
        if (enabled === undefined) {
          const oldLinkedKeyword = oldKeywords.find(
            (oldKeyword) =>
              getKeywordLabel(oldKeyword) === getKeywordLabel(keyword),
          );

          if (
            oldLinkedKeyword &&
            (getKeywordEnabled(oldLinkedKeyword) === true ||
              getKeywordEnabled(oldLinkedKeyword) === false)
          ) {
            return {
              ...keyword,
              enabled: getKeywordEnabled(oldLinkedKeyword),
            };
          }
        }
        return keyword;
      });
      newBody[locale][key] = computedKeywords;
    }
  });

  return {
    ...newState,
    [defaultLocalizableKey]: { ...newState[defaultLocalizableKey], ...newBody },
  };
};

export const multipleKeywordsSync = (
  oldState: ?Object,
  newState: Object,
): Object => {
  if (!oldState) return newState;
  const run = pipe(
    () => keywordsSync(oldState, newState, 'want_to_learn'),
    (a) => keywordsSync(oldState, a, 'knowledgeable_about'),
    (a) => keywordsSync(oldState, a, 'love_talking_about'),
  );

  return run();
};

const initialState = {};

const _updateUserActivity = (state: Object, action: Object) => {
  const { result, userId } = action;
  const user = state[userId];

  if (user) {
    return {
      ...state,
      [`${userId}`]: {
        ...user,
        activity: result,
      },
    };
  }
  return state;
};

const _updateStats = (state: Object, action: Object) => {
  const { result, userId } = action;
  const user = state[userId];

  if (user) {
    return {
      ...state,
      [`${userId}`]: {
        ...user,
        statistics: result,
      },
    };
  }
  return state;
};

const _updateUserAvatar = (state: Object, action: Object) => {
  const { result, userId } = action;
  const user = state[userId];

  if (user) {
    return {
      ...state,
      [`${userId}`]: {
        ...user,
        avatar: result,
      },
    };
  }
  return state;
};

export const _patch = (state: Object, action: Object) =>
  patchEntities(state, action, 'users', (old, merged) =>
    multipleKeywordsSync(old, merged),
  );

const _bookmark = (state: Object, action: Object) => {
  const { id, bookmarked } = action;

  const user = state[id];

  return {
    ...state,
    [`${id}`]: {
      ...user,
      bookmarked,
    },
  };
};

export default createReducer(initialState, {
  [types.FETCH_USER_ACTIVITY_SUCCESS]: _updateUserActivity,
  [types.FETCH_USER_STATISTICS_SUCCESS]: _updateStats,
  [types.UPDATE_AVATAR_SUCCESS]: _updateUserAvatar,
  [types.BOOKMARK_USER_SUCCESS]: _bookmark,
  [entititesTypes.PATCH_ENTITIES]: _patch,
});
